import React, { useState, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Divider,
  Typography,
  Collapse,
  useTheme,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Key as KeyIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  LockReset as ChangePasswordIcon,
  TextFields as TextFieldsIcon,
  Folder as FolderIcon,
  Settings as SettingsIcon,
  AddHome as HomeIcon,
  ExpandLess,
  ExpandMore,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { AuthContext } from '../context/AuthContext';
import { ColorModeContext } from '../App';

const drawerWidth = 240;

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const SidebarDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  backgroundColor: theme.palette.mode === 'dark' ? '#424242' : theme.palette.primary.main, // Dark blue in dark mode
  color: '#ffffff',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  backgroundColor: theme.palette.mode === 'dark' ? '#424242' : theme.palette.primary.main, // Dark blue in dark mode
  color: '#ffffff',
});

const Header = () => {
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { user, logout } = useContext(AuthContext);
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleSettingsToggle = () => {
    setSettingsOpen(!settingsOpen);
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    ...(user.user_group === 'admin'
      ? [
          { text: 'Users', icon: <PeopleIcon />, path: '/users' },
          { text: 'Remote Text', icon: <TextFieldsIcon />, path: '/remote-text' },
          { text: 'File Server', icon: <FolderIcon />, path: '/file-server' },
        ]
      : []),
    { text: 'CDKeys', icon: <KeyIcon />, path: '/cdkeys' },
    { text: 'Change Password', icon: <ChangePasswordIcon />, path: '/change-password' },
    ...(user.user_group === 'admin'
      ? [
          { 
            text: 'Settings', 
            icon: <SettingsIcon />, 
            onClick: handleSettingsToggle,
            subItems: [
              { text: 'SMTP Config', icon: <EmailIcon />, path: '/smtp-config' },
              { text: 'File Server Config', icon: <SettingsIcon />, path: '/file-server-config' },
              { text: 'Home Settings', icon: <HomeIcon />, path: '/home-settings' },
            ]
          },
        ]
      : []),
    { text: 'Logout', icon: <LogoutIcon />, onClick: logout },
    {
      text: 'Toggle Theme',
      icon: theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />,
      onClick: colorMode.toggleColorMode,
    },
  ];

  return (
    <SidebarDrawer variant="permanent" open={open}>
      <DrawerHeader>
        <IconButton onClick={handleDrawerToggle} sx={{ color: '#ffffff' }}>
          {open ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </DrawerHeader>
      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)' }} />
      <Typography variant="h6" sx={{ padding: '16px', color: '#ffffff' }}>
        YYDS
      </Typography>
      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)' }} />
      <List>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItem
              button
              component={item.path ? RouterLink : 'li'}
              to={item.path}
              onClick={item.onClick}
              sx={{ color: '#ffffff' }}
            >
              <ListItemIcon sx={{ color: '#ffffff' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
              {item.subItems && (settingsOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItem>
            {item.subItems && (
              <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <ListItem
                      button
                      key={subItem.text}
                      component={RouterLink}
                      to={subItem.path}
                      sx={{ paddingLeft: 4, color: '#ffffff' }}
                    >
                      <ListItemIcon sx={{ color: '#ffffff' }}>{subItem.icon}</ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </SidebarDrawer>
  );
};

export default Header;